
// A badge that can be displayed on a user's profile. Standard badges are
// pre-populated as static constants below; the class is intentionally
// instantiable so future badges can be added without a code constant (e.g.
// constructed from a database row). The DB only stores each badge's public_id
// (see DBBadgeTable) — all display metadata lives here.
//
// Colours are the names of colour roles in packages/styles/src/theme.css,
// so the web and native apps can each map them. Masters uses dark text since
// white is unreadable on its yellow. Within a family, the higher-priority
// badge is the higher tier, so the display collapses a family to its
// highest-priority badge.
export class Badge {
    public static readonly FINKEL_GRANDMASTER = new Badge(
        "finkel_grandmaster",
        "Finkel Grandmaster",
        "Reached 4000 rating in ranked Finkel",
        "white", "ruleset-finkel",
        1000, "rating:finkel",
    );

    public static readonly BLITZ_GRANDMASTER = new Badge(
        "blitz_grandmaster",
        "Blitz Grandmaster",
        "Reached 4000 rating in ranked Blitz",
        "white", "ruleset-blitz",
        1000, "rating:blitz",
    );

    public static readonly MASTERS_GRANDMASTER = new Badge(
        "masters_grandmaster",
        "Masters Grandmaster",
        "Reached 4000 rating in ranked Masters",
        "on-accent", "ruleset-masters",
        1000, "rating:masters",
    );

    public static readonly FINKEL_MASTER = new Badge(
        "finkel_master",
        "Finkel Master",
        "Reached 3000 rating in ranked Finkel",
        "white", "ruleset-finkel",
        990, "rating:finkel",
    );

    public static readonly BLITZ_MASTER = new Badge(
        "blitz_master",
        "Blitz Master",
        "Reached 3000 rating in ranked Blitz",
        "white", "ruleset-blitz",
        990, "rating:blitz",
    );

    public static readonly MASTERS_MASTER = new Badge(
        "masters_master",
        "Masters Master",
        "Reached 3000 rating in ranked Masters",
        "on-accent", "ruleset-masters",
        990, "rating:masters",
    );

    static values(): Badge[] {
        return [
            Badge.FINKEL_GRANDMASTER,
            Badge.BLITZ_GRANDMASTER,
            Badge.MASTERS_GRANDMASTER,
            Badge.FINKEL_MASTER,
            Badge.BLITZ_MASTER,
            Badge.MASTERS_MASTER,
        ];
    }

    // Instance fields
    private readonly id: string;
    private readonly name: string;
    private readonly description: string;
    private readonly foregroundColour: string;
    private readonly backgroundColour: string;
    private readonly priority: number;
    private readonly familyKey: string | null;

    constructor(
        id: string,
        name: string,
        description: string,
        foregroundColour: string,
        backgroundColour: string,
        priority: number,
        familyKey: string | null,
    ) {
        this.id = id;
        this.name = name;
        this.description = description;
        this.foregroundColour = foregroundColour;
        this.backgroundColour = backgroundColour;
        this.priority = priority;
        this.familyKey = familyKey;
    }

    // Getters
    getID(): string {
        return this.id;
    }

    getName(): string {
        return this.name;
    }

    getDescription(): string {
        return this.description;
    }

    getForegroundColour(): string {
        return this.foregroundColour;
    }

    getBackgroundColour(): string {
        return this.backgroundColour;
    }

    getPriority(): number {
        return this.priority;
    }

    getFamilyKey(): string | null {
        return this.familyKey;
    }

    static getByID(id: string): Badge {
        const badge = Badge.getByIDOrNull(id);
        if (badge === null)
            throw new Error(`Unknown badge ${id}`);
        return badge;
    }

    static getByIDOrNull(id: string | null): Badge | null {
        if (id === null)
            return null;

        const upperID = id.toUpperCase();
        for (const badge of Badge.values()) {
            if (badge.getID().toUpperCase() === upperID)
                return badge;
        }
        return null;
    }
}
